import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, Send, Phone, Globe2, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interests = [
  "Franchise My Brand (Consulting)",
  "Become a Franchise Partner (Broking)",
  "General Enquiry",
  "Partnership / Collaboration",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    interest: "General Enquiry",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thank you! Your enquiry has been received.");
      setForm({ name: "", email: "", phone: "", company: "", interest: "General Enquiry", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Get In Touch"
        description="Contact Franchise India Knowledge Services. Whether you want to franchise your knowledge brand or become a franchise partner, reach us by email, phone or the enquiry form."
        path="/contact"
      />
      {/* HEADER */}
      <section className="bg-navy noise-overlay text-white pt-20 pb-24 md:pt-28 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel className="text-gold">Get In Touch</SectionLabel>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl text-balance">
              Let's start the conversation.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
              Whether you're a brand looking to franchise or an entrepreneur seeking
              a proven opportunity — we'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 md:py-28 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Info */}
          <Reveal className="lg:col-span-4">
            <h2 className="text-2xl md:text-3xl font-semibold text-navy">Reach us directly</h2>
            <p className="mt-4 text-ink-secondary leading-relaxed">
              Our team responds to every enquiry personally.
            </p>
            <div className="mt-10 space-y-8">
              <a
                href="mailto:satyam.garg@franglobal.com"
                data-testid="contact-email-link"
                className="flex items-start gap-4 group"
              >
                <div className="h-12 w-12 shrink-0 flex items-center justify-center bg-navy text-gold group-hover:bg-teal transition-colors">
                  <Mail size={20} />
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wider text-ink-muted font-semibold">Email</div>
                  <div className="text-navy font-medium break-all">satyam.garg@franglobal.com</div>
                </div>
              </a>

              <a
                href="tel:+918376849548"
                data-testid="contact-phone-link"
                className="flex items-start gap-4 group"
              >
                <div className="h-12 w-12 shrink-0 flex items-center justify-center bg-navy text-gold group-hover:bg-teal transition-colors">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wider text-ink-muted font-semibold">Phone</div>
                  <div className="text-navy font-medium">+91-8376849548</div>
                </div>
              </a>

              <a
                href="https://www.linkedin.com/company/franchiseindiaknowledgeservices/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="flex items-start gap-4 group"
              >
                <div className="h-12 w-12 shrink-0 flex items-center justify-center bg-navy text-gold group-hover:bg-teal transition-colors">
                  <Linkedin size={20} />
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wider text-ink-muted font-semibold">LinkedIn</div>
                  <div className="text-navy font-medium">Franchise India Knowledge Services</div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="h-12 w-12 shrink-0 flex items-center justify-center bg-navy text-gold">
                  <Globe2 size={20} />
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wider text-ink-muted font-semibold">Markets</div>
                  <div className="text-navy font-medium">Global</div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1} className="lg:col-span-8">
            {done ? (
              <div
                data-testid="contact-success"
                className="bg-white border border-navy/10 p-10 md:p-14 h-full flex flex-col items-center justify-center text-center"
              >
                <div className="h-16 w-16 flex items-center justify-center bg-teal/10 text-teal">
                  <CheckCircle2 size={32} />
                </div>
                <h3 className="mt-6 text-2xl font-semibold text-navy">Message received!</h3>
                <p className="mt-3 text-ink-secondary max-w-md">
                  Thank you for reaching out. A member of our team will get back to
                  you shortly.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 text-navy font-semibold border-b-2 border-gold pb-1 hover:text-teal transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-white border border-navy/10 p-8 md:p-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-navy font-semibold">Full Name *</Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your name"
                      className="mt-2 rounded-none border-navy/20 focus-visible:ring-teal h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-navy font-semibold">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@company.com"
                      className="mt-2 rounded-none border-navy/20 focus-visible:ring-teal h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-navy font-semibold">Phone</Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+00 00000 00000"
                      className="mt-2 rounded-none border-navy/20 focus-visible:ring-teal h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-navy font-semibold">Company / Brand</Label>
                    <Input
                      id="company"
                      data-testid="contact-company"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      placeholder="Your business"
                      className="mt-2 rounded-none border-navy/20 focus-visible:ring-teal h-12"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <Label className="text-navy font-semibold">I'm interested in</Label>
                  <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                    <SelectTrigger
                      data-testid="contact-interest"
                      className="mt-2 rounded-none border-navy/20 h-12"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {interests.map((it) => (
                        <SelectItem key={it} value={it} data-testid={`interest-option-${it}`}>
                          {it}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-6">
                  <Label htmlFor="message" className="text-navy font-semibold">Message *</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us about your brand or what you're looking for..."
                    rows={5}
                    className="mt-2 rounded-none border-navy/20 focus-visible:ring-teal resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="mt-8 inline-flex items-center justify-center gap-2 bg-navy text-white px-10 py-4 font-semibold tracking-wide hover:bg-navy-light transition-colors disabled:opacity-60 w-full md:w-auto"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
